// GERADO de backend-senne/src/contracts — NÃO editar; rode scripts/sync-contracts.sh

import { z } from 'zod'

/**
 * Erros de aplicação (spec 008, US3) — a forma do fio, compartilhada.
 *
 * Mora em `@contracts` porque três lados leem a mesma coisa: o validador da
 * ingestão, a consulta, e a tela do portal. Redefinir a forma em qualquer um
 * deles cria duas verdades que divergem na próxima mudança (rule `validation.md`).
 */

export const appErrorOriginSchema = z.enum(['frontend', 'backend'])
export type AppErrorOrigin = z.infer<typeof appErrorOriginSchema>

/**
 * Sem `info`: log informativo não é erro, e aceitá-lo transformaria a tabela em
 * depósito de telemetria com retenção de 30 dias pagando o custo.
 */
export const appErrorSeveritySchema = z.enum(['warn', 'error', 'fatal'])
export type AppErrorSeverity = z.infer<typeof appErrorSeveritySchema>

/** Derivado do `runtimeEnvironment` do servidor, nunca do cliente. */
export const appErrorEnvironmentSchema = z.enum([
    'development',
    'staging',
    'production',
])
export type AppErrorEnvironment = z.infer<typeof appErrorEnvironmentSchema>

/**
 * Limites de tamanho. São os mesmos das colunas em `app_error_logs`, e existem
 * aqui para que a recusa aconteça na fronteira, com 400 e mensagem, em vez de
 * virar erro de driver na hora do INSERT — ou, pior, truncamento silencioso.
 */
export const MESSAGE_LIMIT = 500
export const ROUTE_LIMIT = 255
export const REFERENCE_LIMIT = 64
/** `context` serializado. `json` no MySQL aguenta muito mais; o teto é de bom senso. */
export const SERIALIZED_CONTEXT_LIMIT = 4000

/**
 * Corpo de `POST /api/observability/errors`.
 *
 * O que NÃO está aqui é a parte importante: `id_user`, `role`, `ip`,
 * `user_agent` e `environment` são derivados do servidor. Cliente é dado, não
 * instrução — mandar esses campos não tem efeito nenhum, porque a gravação lê
 * apenas a saída deste parse.
 *
 * `strip` (default do zod) em vez de `.strict()` de propósito: o caminho de
 * ingestão de erro não deve falhar porque o cliente mandou um campo a mais —
 * isso perderia justamente o erro que se queria registrar.
 */
export const registerErrorBodySchema = z.object({
    origin: appErrorOriginSchema,
    severity: appErrorSeveritySchema,
    message: z.string().trim().min(1).max(MESSAGE_LIMIT),
    route: z.string().max(ROUTE_LIMIT).optional(),
    reference: z.string().max(REFERENCE_LIMIT).optional(),
    context: z
        .record(z.unknown())
        .refine(
            (v) => JSON.stringify(v).length <= SERIALIZED_CONTEXT_LIMIT,
            `context excede ${SERIALIZED_CONTEXT_LIMIT} caracteres serializados`
        )
        .optional(),
})
export type RegisterErrorBody = z.infer<typeof registerErrorBodySchema>

const emptyToUndefined = (v: unknown) => (v === '' ? undefined : v)
const dateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida (YYYY-MM-DD)')

/**
 * Query de `GET /api/observability/errors`.
 *
 * `startDate`/`endDate`/`page`/`perPage` para casar com `GET /api/audit/access`,
 * que já está commitado e é a outra aba da MESMA tela. O `contracts/README.md`
 * escreveu `de`/`ate`/`pageNo`/`pageSize`; duas convenções na mesma tela custam
 * mais do que a divergência com o documento, que fica registrada aqui.
 */
export const listErrorsQuerySchema = z.object({
    startDate: z.preprocess(emptyToUndefined, dateSchema.optional()),
    endDate: z.preprocess(emptyToUndefined, dateSchema.optional()),
    severity: z.preprocess(
        emptyToUndefined,
        appErrorSeveritySchema.optional()
    ),
    origin: z.preprocess(emptyToUndefined, appErrorOriginSchema.optional()),
    /** Casa por prefixo: rota tem id no meio, igualdade exata não serve. */
    route: z.preprocess(
        emptyToUndefined,
        z.string().max(ROUTE_LIMIT).optional()
    ),
    page: z.preprocess(
        emptyToUndefined,
        z.coerce.number().int().positive().default(1)
    ),
    perPage: z.preprocess(
        emptyToUndefined,
        z.coerce.number().int().positive().max(100).default(20)
    ),
})
export type ListErrorsQuery = z.infer<typeof listErrorsQuerySchema>

/** Um erro como sai na resposta. camelCase, igual ao `AccessTrailItem` da US2. */
export interface AppErrorLogItem {
    id: number
    origin: AppErrorOrigin
    severity: AppErrorSeverity
    message: string
    route: string | null
    reference: string | null
    /** Quem estava logado quando o erro ocorreu. `null` = conta removida depois. */
    userId: number | null
    /** `role_id` do ator no momento do erro. */
    role: number | null
    userAgent: string | null
    ip: string | null
    context: Record<string, unknown> | null
    environment: AppErrorEnvironment
    occurredAt: Date
}

export interface ListErrorsResponse {
    items: AppErrorLogItem[]
    pagination: {
        total: number
        page: number
        perPage: number
        totalPages: number
    }
}
